<template>
  <div class="font-inter min-h-screen bg-gray-50 dark:bg-background pb-10">
    <div class="max-w-[1400px] mx-auto px-4 pt-8">
      <!-- Header -->
      <div class="mb-8">
        <h1 v-if="loading" class="text-[32px] font-semibold text-foreground">
          Loading...
        </h1>
        <div v-else>
          <h1 class="text-[40px] text-foreground">
            Welcome back, {{ user.first_name || "User" }}
          </h1>
          <p class="text-[20px] text-muted-foreground mt-1">
            Continue learning or explore new opportunities today.
          </p>
        </div>
      </div>

      <!-- Main Grid -->
      <div class="grid grid-cols-1 lg:grid-cols-12 gap-6 items-stretch">
        <div class="lg:col-span-6 space-y-6">
          <GoalsStatusCard
            class="h-full"
            :total="totalGoalsCount"
            :completed="completedGoalsCount"
            :not-started="notStartedGoalsCount"
            :active="activeGoalsCount"
            :recent-learning-goal="recentLearningGoal"
            :continue-learning-progress="continueLearningProgress"
            :continue-learning-meta="continueLearningMeta"
            @continue-learning="goToContinueLearning"
          />
        </div>

        <div class="lg:col-span-6 space-y-6">
          <JobsCard class="h-full" :jobs="jobs" :loading="loadingJobs" />
        </div>

        <!-- <div class="lg:col-span-4 space-y-6">
          <UpsideCard :limit="1" />
        </div> -->
      </div>

      <div class="mt-6">
        <FeedAboutSection
          :user-avatar="userAvatar"
          :user-name="userName"
          :date-joined="dateJoined"
          :people-to-follow="peopleToFollow"
        />
      </div>
    </div>
  </div>
</template>

<script>
import apiClient from "@/axios";
import GoalsStatusCard from "@/components/dashboard/GoalsStatusCard.vue";
import JobsCard from "@/components/dashboard/JobsCard.vue";
// import UpsideCard from "@/components/dashboard/UpsideCard.vue";
import FeedAboutSection from "@/components/dashboard/FeedAboutSection.vue";

export default {
  name: "TalentDashboardComponent",
  components: {
    GoalsStatusCard,
    JobsCard,
    // UpsideCard,
    FeedAboutSection,
  },
  data() {
    return {
      user: {},
      loading: true,

      learningGoals: [],
      loadingGoals: true,

      goalsSummary: null,
      loadingSummary: true,

      recentLearningGoal: null,
      loadingRecentGoal: true,

      jobs: [],
      loadingJobs: true,

      sampleJobs: [], // Keeping for reference if needed, but using live jobs

      peopleToFollow: [
        {
          id: 1,
          name: "Lisa Balogun",
          role: "Aspiring Product Designer",
          avatar: require("@/assets/users/default-profile-picture.jpg"),
        },
        {
          id: 2,
          name: "Lisa Balogun",
          role: "Aspiring Product Designer",
          avatar: require("@/assets/users/default-profile-picture.jpg"),
        },
      ],
    };
  },
  computed: {
    totalGoalsCount() {
      return this.goalsSummary?.total_learning_goals || 0;
    },
    completedGoalsCount() {
      return this.goalsSummary?.total_completed || 0;
    },
    notStartedGoalsCount() {
      return this.goalsSummary?.total_not_started || 0;
    },
    activeGoalsCount() {
      return this.goalsSummary?.total_active || 0;
    },
    completedGoalsPercentage() {
      return this.totalGoalsCount > 0
        ? (this.completedGoalsCount / this.totalGoalsCount) * 100
        : 0;
    },
    notStartedPercentage() {
      return this.totalGoalsCount > 0
        ? (this.notStartedGoalsCount / this.totalGoalsCount) * 100
        : 0;
    },
    activeGoalsPercentage() {
      return this.totalGoalsCount > 0
        ? (this.activeGoalsCount / this.totalGoalsCount) * 100
        : 0;
    },

    continueLearningMeta() {
      const count = this.recentLearningGoal?.incomplete_subtasks;
      if (!count) return "No lessons left";
      return `${count} lesson${count !== 1 ? "s" : ""} left`;
    },

    continueLearningProgress() {
      const p = this.recentLearningGoal?.progress;
      if (typeof p !== "number") return 0;
      return Math.max(0, Math.min(100, Math.round(p)));
    },

    userAvatar() {
      const user = this.$store.state.user;
      return (
        user?.profile_picture ||
        require("@/assets/users/default-profile-picture.jpg")
      );
    },
    userName() {
      const user = this.$store.state.user;
      const fullName = [user?.first_name, user?.last_name]
        .filter(Boolean)
        .join(" ");
      return user?.name || fullName;
    },
    userRole() {
      return "Talent";
    },
    dateJoined() {
      const user = this.$store.state.user;
      if (user?.created_at) {
        const date = new Date(user.created_at);
        return date.toLocaleDateString("en-US", {
          day: "numeric",
          month: "long",
          year: "numeric",
        });
      }
      return "";
    },
  },
  methods: {
    goToContinueLearning() {
      if (this.recentLearningGoal) {
        this.$router.push(
          `/learning-goals/${this.recentLearningGoal.project_id}`,
        );
      } else {
        this.$router.push("/learning-goals");
      }
    },

    async fetchCurrentUser() {
      try {
        const response = await apiClient.post("/current_user");
        this.user = response.data.user || {};
      } catch (error) {
        console.error("Error fetching user data:", error);
      } finally {
        this.loading = false;
      }
    },

    async fetchGoalsSummary() {
      try {
        this.loadingSummary = true;
        const response = await apiClient.get("/web/learning-goals/summary");
        this.goalsSummary = response.data || null;
        console.log("Goals Summary:", this.goalsSummary);
      } catch (error) {
        console.error("Error fetching goals summary:", error);
        this.goalsSummary = null;
      } finally {
        this.loadingSummary = false;
      }
    },

    async fetchLearningGoals() {
      try {
        this.loadingGoals = true;
        const response = await apiClient.get(
          "/web/learning-goals/my-learning-goals",
        );
        this.learningGoals = response.data || [];
      } catch (error) {
        console.error("Error fetching learning goals:", error);
      } finally {
        this.loadingGoals = false;
      }
    },

    async fetchRecentLearningGoal() {
      try {
        this.loadingRecentGoal = true;
        const response = await apiClient.get("/web/dashboard/talent/recent");
        this.recentLearningGoal = response.data || null;
      } catch (error) {
        console.error("Error fetching recent learning goal:", error);
        this.recentLearningGoal = null;
      } finally {
        this.loadingRecentGoal = false;
      }
    },
    async fetchJobs() {
      try {
        this.loadingJobs = true;
        const response = await apiClient.get("/web/jobs/", {
          params: { limit: 5 },
        });
        if (response.data.status_code === 200) {
          this.jobs = response.data.data.jobs.map((job) => ({
            id: job.id,
            company: job.company,
            position: job.title,
            postedTime: this.getPostedTime(job.created_at),
            location: job.location,
          }));
        }
      } catch (error) {
        console.error("Error fetching jobs:", error);
      } finally {
        this.loadingJobs = false;
      }
    },
    getPostedTime(dateString) {
      if (!dateString) return "Recently";
      const now = new Date();
      const postedDate = new Date(dateString);
      const diffInMilliseconds = now - postedDate;
      const diffInSeconds = Math.floor(diffInMilliseconds / 1000);
      const diffInMinutes = Math.floor(diffInSeconds / 60);
      const diffInHours = Math.floor(diffInMinutes / 60);
      const diffInDays = Math.floor(diffInHours / 24);

      if (diffInDays > 0)
        return `Posted ${diffInDays} day${diffInDays !== 1 ? "s" : ""} ago`;
      if (diffInHours > 0)
        return `Posted ${diffInHours} hour${diffInHours !== 1 ? "s" : ""} ago`;
      if (diffInMinutes > 0)
        return `Posted ${diffInMinutes} minute${
          diffInMinutes !== 1 ? "s" : ""
        } ago`;
      return "Posted just now";
    },
  },
  created() {
    this.fetchCurrentUser();
    this.fetchGoalsSummary();
    this.fetchLearningGoals();
    this.fetchRecentLearningGoal();
    this.fetchJobs();
  },
};
</script>
